/**
 * Keyboard device (§3.1). Digital move, dash, pause, level-up 1/2/3, reroll, restart.
 * Held state is a bitmask over `ACTIONS`; edges are latched with the sim tick they landed on so
 * §3.6 buffering counts ticks, not wall clock.
 */

import { ACTIONS, BindingTable, type ActionId } from './bindings';

/** Codes the page must never act on itself (scroll, quick-find, back-navigation). */
const SWALLOW = new Set<string>([
  'k:Space',
  'k:ArrowUp',
  'k:ArrowDown',
  'k:ArrowLeft',
  'k:ArrowRight',
  'k:Tab',
  'k:Slash',
  'k:Backspace',
]);

export class KeyboardDevice {
  /** Bitmask of currently-held actions. */
  heldMask = 0;
  /** Bitmask of actions whose press edge has not been drained yet. */
  pressedMask = 0;
  /** Sim tick of the latest un-drained press, per action index. */
  readonly pressTick = new Int32Array(ACTIONS.length).fill(-1);
  activity = false;

  private held = new Set<string>();
  private tick = 0;
  private revision = -1;
  private disposed = false;

  private readonly onKeyDown: (ev: KeyboardEvent) => void;
  private readonly onKeyUp: (ev: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  constructor(private bindings: BindingTable) {
    this.onKeyDown = (ev) => this.handleDown(ev);
    this.onKeyUp = (ev) => this.handleUp(ev);
    this.onBlur = () => this.releaseAll();
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onKeyDown, { passive: false });
      window.addEventListener('keyup', this.onKeyUp, { passive: false });
      window.addEventListener('blur', this.onBlur);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onBlur);
    }
  }

  setTick(tick: number): void {
    this.tick = tick;
    if (this.revision !== this.bindings.revision) {
      this.revision = this.bindings.revision;
      this.recomputeHeld();
    }
  }

  /** Drains the press edges; call once per sim tick after `setTick`. */
  drainPressed(): number {
    const m = this.pressedMask;
    this.pressedMask = 0;
    return m;
  }

  isHeld(action: ActionId): boolean {
    return (this.heldMask & (1 << ACTIONS.indexOf(action))) !== 0;
  }

  releaseAll(): void {
    this.held.clear();
    this.heldMask = 0;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('keyup', this.onKeyUp);
      window.removeEventListener('blur', this.onBlur);
    }
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onBlur);
    this.releaseAll();
  }

  /** Test/headless hook: inject a press without a DOM event. */
  injectDown(code: string, tick: number): void {
    this.tick = tick;
    this.applyDown(code);
  }

  /** Test/headless hook: inject a release without a DOM event. */
  injectUp(code: string): void {
    this.applyUp(code);
  }

  private handleDown(ev: KeyboardEvent): void {
    if (isTextEntry(ev.target)) return;
    const code = 'k:' + ev.code;
    const bound = this.bindings.actionsFor('keyboard', code) !== 0;
    if (bound || SWALLOW.has(code)) ev.preventDefault();
    if (ev.repeat) return;
    this.applyDown(code);
  }

  private handleUp(ev: KeyboardEvent): void {
    if (isTextEntry(ev.target)) return;
    this.applyUp('k:' + ev.code);
  }

  private applyDown(code: string): void {
    if (this.held.has(code)) return;
    this.held.add(code);
    const mask = this.bindings.actionsFor('keyboard', code);
    if (mask === 0) return;
    this.activity = true;
    this.heldMask |= mask;
    this.pressedMask |= mask;
    for (let i = 0; i < ACTIONS.length; i++) {
      if (mask & (1 << i)) this.pressTick[i] = this.tick;
    }
  }

  private applyUp(code: string): void {
    if (!this.held.delete(code)) return;
    this.recomputeHeld();
  }

  private recomputeHeld(): void {
    let m = 0;
    for (const code of this.held) m |= this.bindings.actionsFor('keyboard', code);
    this.heldMask = m;
  }
}

function isTextEntry(t: EventTarget | null): boolean {
  if (!t || typeof (t as Element).tagName !== 'string') return false;
  const el = t as HTMLElement;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
}
